import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Paper, Button, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import endpoints from "../endpoint";
import { getImageUrl } from "../utils/helpFunction";

const EbookPage = () => {
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [pages, setPages] = useState([{ title: "", content: "" }]); // Page List
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEbooks = async () => {
      try {
        const response = await axios.get(
          `${endpoints.serverBaseUrl}api/ebook`,
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          }
        );
        setEbooks(response.data);
      } catch (err) {
        setError("Failed to load ebooks");
      } finally {
        setLoading(false);
      }
    };
    fetchEbooks();
  }, []);

  // **Handle Image Upload Process**
  const handleImageUpload = async () => {
    if (!selectedImage) {
      alert("Please select an image first.");
      return null;
    }

    try {
      // 1. Get Presigned URL
      const uploadResponse = await axios.post(
        `${endpoints.serverBaseUrl}api/generate-presigned-url`,
        {
          fileName: selectedImage.name,
          fileType: selectedImage.type,
        },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );

      if (!uploadResponse.data.presignedUrl) {
        throw new Error("Failed to get presigned URL.");
      }

      const { presignedUrl, publicId, cloudflareBaseUrl } = uploadResponse.data;

      // 2. Upload Image to Cloudflare
      await axios.put(presignedUrl, selectedImage, {
        headers: { "Content-Type": selectedImage.type },
      });

      return { publicId, presignedUrl, cloudflareBaseUrl }; // Return full response
    } catch (error) {
      console.error("Image upload failed:", error);
      alert("Failed to upload image.");
      return null;
    }
  };

  // **Handle Ebook Submission**
  const handleSubmit = async () => {
    if (!title) {
      alert("Please enter a title.");
      return;
    }

    const uploadData = await handleImageUpload(); // Upload image first

    if (!uploadData) return;

    const { publicId, presignedUrl, cloudflareBaseUrl } = uploadData;

    const ebookData = {
      title,
      image: publicId, // Store the publicId as the image
      presignedUrl,
      cloudflareBaseUrl,
      pages,
    };

    const token = sessionStorage.getItem("token");

    try {
      await axios.post(`${endpoints.serverBaseUrl}api/ebook`, ebookData, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
      });

      alert("Ebook added successfully!");
      setTitle(""); // Reset form
      setSelectedImage(null);
      setPages([{ title: "", content: "" }]); // Reset pages
    } catch (error) {
      console.error("Ebook upload failed:", error);
      alert("Failed to add ebook.");
    }
  };

  // **Handle Page Field Change**
  const handlePageChange = (index, field, value) => {
    const updatedPages = [...pages];
    updatedPages[index][field] = value;
    setPages(updatedPages);
  };

  // **Add New Page**
  const addPage = () => {
    setPages([...pages, { title: "", content: "" }]);
  };

  if (loading) return <Typography sx={{ p: 2 }}>Loading ebooks...</Typography>;
  if (error)
    return <Typography sx={{ p: 2, color: "red" }}>{error}</Typography>;

  return (
    <Box sx={{ pl: 1, pr: 1, mb: 9 }}>
      {/* Header Section */}
      <Box
        pr={2}
        pb={1}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#E9EBEB",
        }}
      >
        <Typography
          sx={{ fontWeight: "bold", mt: "0.5rem", mb: "0.5rem" }}
          variant="body1"
        >
          The Trader’s Library
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "#007BFF",
            cursor: "pointer",
          }}
          onClick={() => console.log("See All Clicked")}
        >
          See All
        </Typography>
      </Box>

      {/* Ebook Upload Form */}
      <Box sx={{ mb: 3 }}>
        <TextField
          label="Ebook Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setSelectedImage(e.target.files[0])}
          style={{ marginBottom: "10px" }}
        />

        {/* Page Fields */}
        {pages.map((page, index) => (
          <Box key={index} sx={{ mb: 2, p: 2, border: "1px solid #ddd" }}>
            <TextField
              label={`Page ${index + 1} Title`}
              fullWidth
              value={page.title}
              onChange={(e) => handlePageChange(index, "title", e.target.value)}
              sx={{ mb: 1 }}
            />
            <TextField
              label={`Page ${index + 1} Content`}
              fullWidth
              multiline
              rows={3}
              value={page.content}
              onChange={(e) =>
                handlePageChange(index, "content", e.target.value)
              }
            />
          </Box>
        ))}

        {/* Add More Pages Button */}
        <Button variant="outlined" sx={{ mb: 2 }} onClick={addPage}>
          + Add Page
        </Button>

        {/* Submit Button */}
        <Button variant="contained" onClick={handleSubmit}>
          Upload Ebook
        </Button>
      </Box>
    </Box>
  );
};

export default EbookPage;
